import { useCallback, useEffect, useState } from "react";
import { If } from "react-conditional-components-renderer";
import classNames from "classnames";
import { toast } from "react-toastify";

import AscModal from "../AscModal/AscModal";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscAddModuleContent from "./AscAddModuleContent/AscAddModuleContent";
import AscAddModuleDetails from "./AscAddModuleDetails/AscAddModuleDetails";
import AscAddModuleInfo from "./AscAddModuleInfo/AscAddModuleInfo";
import AscAddModuleActivities from "./AscAddModuleActivities/AscAddModuleActivities";
import AscAddFeedback from "./AscAddFeedback/AscAddFeedback";
import AscAddSurvey from "./AscAddSurvey/AscAddSurvey";
import { AscErrorBoundary, AscLoading } from "@ascendnow/ascend-components";
import { CloseIcon } from "../AscIcons";

import styles from "./AscActivitiesAddModule.module.scss";
import {
  ActivityImage,
  AdminActivity,
  AdminActivityApi,
  AdminActivityType,
  AdminChallenge,
  AdminEditModule,
  AdminFeedbackItemMap,
  AdminFlashcardsFromApi,
  AdminModuleContent,
  AdminModuleContentForm,
  AdminModuleInfo,
  AdminQuizDragAndDropFromApi,
  AdminSurvey,
  Caption,
} from "@ascendnow/shared";
import {
  getAdminModuleContentChanges,
  getAdminModuleContentFileChanges,
  getAdminModuleContentVideoChanges,
  getAdminModuleInfoChanges,
  getAdminModuleSurveyChanges,
} from "./helpers";

export interface AscActivitiesAddModuleProps {
  className?: string;
  visible: boolean;
  header: string;
  challenges?: AdminChallenge[];
  setVisible: (visible: boolean) => void;
  addModule: (moduleObj: AdminModuleInfo) => Promise<string>;
  addContentVideo: (video: File, moduleId: string) => Promise<void>;
  addContentFile: (file: File, moduleId: string) => Promise<void>;
  addContent: (
    contentObj: AdminModuleContent,
    moduleId: string
  ) => Promise<void>;
  currentModule?: AdminEditModule;
  updateModule?: (
    moduleObj: AdminModuleInfo,
    moduleId: string
  ) => Promise<void>;
  updateContentVideo?: (video: any, videoId: string) => Promise<void>;
  updateContentFile?: (file: any, fileId: string) => Promise<void>;
  updateContent?: (
    contentObj: Partial<AdminModuleContent>,
    contentId: string
  ) => Promise<void>;
  addActivity?: <T extends AdminActivityType>(
    moduleId: string,
    type: AdminActivityType,
    activity: AdminActivityApi<T>,
    image?: ActivityImage[],
    orderId?: string
  ) => Promise<void>;
  updateActivity?: <T extends AdminActivityType>(
    activityId: string,
    type: AdminActivityType,
    activity: AdminActivityApi<T>,
    image?: ActivityImage[],
    orderId?: string
  ) => Promise<void>;
  addSurvey: (moduleObj: AdminSurvey, moduleId: string) => Promise<void>;
  updateModuleSurvey?: (
    moduleObj: Partial<AdminSurvey>,
    surveyId: string
  ) => Promise<void>;
  addFeedback?: (moduleId: string) => Promise<string>;
  addFeedbackApp?: (obj: any, feedbackId: string) => Promise<void>;
  updateFeedbackApp?: (obj: any, appId: string) => Promise<void>;
  deleteFeedbackApp?: (appId: string) => Promise<void>;
  addFeedbackMovie?: (obj: any, feedbackId: string) => Promise<void>;
  updateFeedbackMovie?: (obj: any, movieId: string) => Promise<void>;
  deleteFeedbackMovie?: (movieId: string) => Promise<void>;
  addFeedbackPodcast?: (obj: any, feedbackId: string) => Promise<void>;
  updateFeedbackPodcast?: (obj: any, podcastId: string) => Promise<void>;
  deleteFeedbackPodcast?: (podcastId: string) => Promise<void>;
  addFeedbackYoutube?: (obj: any, feedbackId: string) => Promise<void>;
  updateFeedbackYoutube?: (obj: any, youtubeId: string) => Promise<void>;
  deleteFeedbackYoutube?: (youtubeId: string) => Promise<void>;
  addFeedbackBook?: (obj: any, feedbackId: string) => Promise<void>;
  updateFeedbackBook?: (obj: any, bookId: string) => Promise<void>;
  deleteFeedbackBook?: (bookId: string) => Promise<void>;
  deleteContentFile: (fileId: string) => Promise<void>;
}

export default function AscActivitiesAddModule({
  className,
  visible,
  header,
  challenges,
  currentModule,
  setVisible,
  addModule,
  addContentVideo,
  addContentFile,
  addContent,
  addActivity,
  addSurvey,
  addFeedback,
  addFeedbackApp,
  addFeedbackMovie,
  addFeedbackPodcast,
  addFeedbackYoutube,
  addFeedbackBook,
  updateContent,
  updateContentVideo,
  updateModule,
  updateModuleSurvey,
  updateActivity,
  updateFeedbackApp,
  updateFeedbackBook,
  updateFeedbackMovie,
  updateFeedbackPodcast,
  updateFeedbackYoutube,
  deleteFeedbackApp,
  deleteFeedbackBook,
  deleteFeedbackMovie,
  deleteFeedbackPodcast,
  deleteFeedbackYoutube,
  deleteContentFile,
}: AscActivitiesAddModuleProps) {
  const [value, setValue] = useState(1);
  const [loading, setLoading] = useState(false);
  const [currentSurveyInfo, setCurrentSurveyInfo] = useState<AdminSurvey>();
  const [currentContentObj, setCurrentContentObj] =
    useState<AdminModuleContent>();
  const [currentContentVideo, setCurrentContentVideo] = useState<
    File | string
  >();
  const [currentContentVideoCaptions, setCurrentContentVideoCaptions] =
    useState<Caption[]>([]);
  const [currentModuleInfo, setCurrentModuleInfo] = useState<AdminModuleInfo>();
  const [currentContentFile, setCurrentContentFile] =
    useState<AdminModuleContentForm["file"]>();
  const [currentActivitiesObj, setCurrentActivitiesObj] = useState<
    AdminActivity[]
  >([]);
  const [currentFeedbackObj, setCurrentFeedbackObj] =
    useState<AdminFeedbackItemMap>();

  const feedbacksMapper = {
    app: addFeedbackApp,
    movie: addFeedbackMovie,
    podcast: addFeedbackPodcast,
    youtube: addFeedbackYoutube,
    book: addFeedbackBook,
  };

  const feedbacksUpdateMapper: any = {
    app: updateFeedbackApp,
    movie: updateFeedbackMovie,
    podcast: updateFeedbackPodcast,
    youtube: updateFeedbackYoutube,
    book: updateFeedbackBook,
  };

  const handleModalData = (moduleObj: AdminModuleInfo) => {
    setCurrentModuleInfo(moduleObj);
  };

  const handleSurveyData = (moduleObj: AdminSurvey) => {
    setCurrentSurveyInfo(moduleObj);
  };

  const handleContentData = (
    contentObj: AdminModuleContent,
    video?: File | string,
    file?: AdminModuleContentForm["file"]
  ) => {
    setCurrentContentObj(contentObj);
    setCurrentContentFile(file);
    if (typeof video !== "string") {
      setCurrentContentVideo(video);
    }
  };

  const handleActivitiesData = (activity: AdminActivity, index: number) => {
    setCurrentActivitiesObj((prev) => {
      const activities = [...prev];
      activities[index] = activity;
      return activities;
    });
  };

  const handleFeedbackData = (moduleObj: any) => {
    setCurrentFeedbackObj(moduleObj);
  };

  const createAllModule = async () => {
    if (!currentModuleInfo) return;
    const moduleId = await addModule?.(currentModuleInfo);

    if (moduleId) {
      currentSurveyInfo && (await addSurvey(currentSurveyInfo, moduleId));
      currentContentObj && (await addContent(currentContentObj, moduleId));
      currentContentVideo &&
        typeof currentContentVideo !== "string" &&
        (await addContentVideo(currentContentVideo, moduleId));

      if (currentContentFile && currentContentFile.length > 0) {
        addContentFile &&
          currentContentFile?.map(
            async (file) => await addContentFile(file, moduleId)
          );
      }
      if (currentActivitiesObj && currentActivitiesObj.length > 0) {
        for (const activity of currentActivitiesObj) {
          let index = 0;
          for (let i = 0; i < currentActivitiesObj.length; i++) {
            if (currentActivitiesObj[i].type == activity.type) {
              index = i;
            }
          }
          await addActivity?.(
            moduleId,
            activity?.type,
            {
              type: activity?.type,
              attemptsNum: activity?.attemptsNum,
              ...(activity?.questions || {}),
            },
            activity?.image,
            index.toString()
          );
        }
      }

      // Feedback
      if (currentFeedbackObj) {
        const feedbackId = await addFeedback?.(moduleId);

        if (feedbackId) {
          const promises = () =>
            Object.keys(currentFeedbackObj).reduce(
              (acc: Promise<void>[], tab) => {
                const _tab = tab as keyof typeof currentFeedbackObj;
                if (currentFeedbackObj?.[_tab]?.length) {
                  const call = currentFeedbackObj?.[_tab]?.map(
                    async (obj) =>
                      await feedbacksMapper[_tab]?.(obj, feedbackId)
                  );

                  return [...acc, ...(call || [])];
                }
                return acc;
              },
              []
            );

          await Promise.all(promises());
        }
      }
    }
  };

  const updateAllModule = async ({
    currentFeedbackObj,
  }: {
    currentFeedbackObj?: AdminFeedbackItemMap;
  }) => {
    if (!currentModule?.id) return;

    // Module info
    const moduleInfoChanges = getAdminModuleInfoChanges(
      currentModuleInfo,
      currentModule
    );

    if (moduleInfoChanges?.hasChanges) {
      await updateModule?.(moduleInfoChanges?.changes, currentModule?.id);
    }

    // Survey
    const surveyChanges = getAdminModuleSurveyChanges(
      currentSurveyInfo,
      currentModule
    );
    // create new survey, if there was no survey previously
    if (surveyChanges?.new) {
      await addSurvey(surveyChanges.changes as AdminSurvey, currentModule?.id);
    }
    // update survey, if there was a survey previously and it was edited
    else if (surveyChanges?.hasChanges && currentModule?.survey?.id) {
      await updateModuleSurvey?.(
        surveyChanges?.changes,
        currentModule?.survey?.id
      );
    }

    // Content (Dig Deeper, Reflection, Challenge)
    const contentChanges = getAdminModuleContentChanges(
      currentContentObj,
      currentModule
    );

    // create new content, if there was no content previously
    if (contentChanges?.new && contentChanges?.changes) {
      await addContent(contentChanges?.changes, currentModule?.id);
    }
    // update content, if there was a content previously
    else if (
      contentChanges?.hasChanges &&
      contentChanges?.changes &&
      currentModule?.content?.id
    ) {
      await updateContent?.(
        contentChanges?.changes,
        currentModule?.content?.id
      );
    }

    //Content Video
    const contentVideoChanges = getAdminModuleContentVideoChanges(
      currentContentVideo,
      currentModule
    );

    // create new content video, if there was no content video previously
    if (contentVideoChanges?.new && contentVideoChanges?.changes) {
      await addContentVideo(contentVideoChanges?.changes, currentModule?.id);
    }
    // update content video, if there was a content video previously
    else if (
      contentVideoChanges?.hasChanges &&
      contentVideoChanges?.changes &&
      currentModule?.moduleVideo?.id
    ) {
      await updateContentVideo?.(
        contentVideoChanges?.changes,
        currentModule?.moduleVideo?.id
      );
    }

    //Content File
    const contentFileChanges = getAdminModuleContentFileChanges(
      currentContentFile,
      currentModule
    );
    // upload newly added files
    if (
      contentFileChanges?.hasChanges &&
      contentFileChanges?.changes?.addedFiles?.length
    ) {
      const moduleId = currentModule?.id;
      contentFileChanges?.changes?.addedFiles?.map(
        async (file) => await addContentFile(file, moduleId)
      );
    }
    // delete removed files
    if (
      contentFileChanges?.hasChanges &&
      contentFileChanges?.changes?.removedFiles?.length
    ) {
      contentFileChanges?.changes?.removedFiles?.map(
        async (file) => file?.id && (await deleteContentFile(file?.id))
      );
    }

    // Activities
    if (currentActivitiesObj && currentActivitiesObj.length > 0) {
      if (currentActivitiesObj && currentActivitiesObj.length > 0) {
        for (const { id, ...activity } of currentActivitiesObj) {
          if (!id) {
            let index = 0;
            for (let i = 0; i < currentActivitiesObj.length; i++) {
              if (currentActivitiesObj[i].type == activity.type) {
                index = i;
              }
            }
            await addActivity?.(
              currentModule?.id,
              activity.type,
              {
                type: activity?.type,
                attemptsNum: activity?.attemptsNum,
                ...activity.questions,
              },
              activity?.image,
              index.toString()
            );
          } else {
            let index = 0;
            for (let i = 0; i < currentActivitiesObj.length; i++) {
              if (currentActivitiesObj[i].type == activity.type) {
                index = i;
              }
            }
            await updateActivity?.(
              id,
              activity.type,
              {
                type: activity.type,
                attemptsNum: activity.attemptsNum,
                ...activity.questions,
              },
              activity?.image,
              index.toString()
            );
          }
        }
      }
    }

    //Feedback
    if (currentFeedbackObj) {
      const promises = () =>
        Object.keys(currentFeedbackObj).reduce((acc: any, tab) => {
          const _tab = tab as keyof typeof currentFeedbackObj;

          if (currentFeedbackObj?.[_tab]?.length) {
            const call = currentFeedbackObj?.[_tab]?.map((obj) =>
              !!obj?.create
                ? currentModule?.feedback?.id
                  ? feedbacksMapper?.[_tab]?.(obj, currentModule?.feedback?.id)
                  : undefined
                : feedbacksUpdateMapper?.[_tab](obj, obj.id)
            );

            return [...acc, ...(call || [])];
          }
          return acc;
        }, []);

      await Promise.all(promises());
    }
  };

  function closeModal() {
    setVisible(false);
  }

  const beforeCreateModule = async () => {
    setLoading(true);
    await createAllModule();
    setLoading(false);
    closeModal();
    setValue(1);
  };

  const beforeUpdateModule = async ({
    currentFeedbackObj,
  }: {
    currentFeedbackObj?: AdminFeedbackItemMap;
  }) => {
    setLoading(true);
    await updateAllModule({ currentFeedbackObj });
    toast?.success(`Module update successfully.`);
    setLoading(false);
    closeModal();
    setValue(1);
  };

  useEffect(() => {
    if (value === 6) {
      currentModule !== null
        ? beforeUpdateModule({ currentFeedbackObj })
        : beforeCreateModule();
    }
  }, [, currentFeedbackObj]);

  useEffect(() => {
    const initialModuleInfo = () => {
      setCurrentModuleInfo({
        id: currentModule?.id,
        name: currentModule?.name,
        coinsNum: currentModule?.coinsNum,
        pointsNum: currentModule?.pointsNum,
        image: currentModule?.thumbnail,
        description: currentModule?.description,
      });
    };

    const initialModuleSurvey = () => {
      if (currentModule?.survey) {
        setCurrentSurveyInfo(currentModule?.survey);
      }
    };

    const initialModuleContent = () => {
      if (currentModule?.content) {
        const { challenge, ...other } = currentModule.content;
        setCurrentContentObj(currentModule?.content);
        setCurrentContentVideo(currentModule?.moduleVideo?.location);
        setCurrentContentVideoCaptions(
          currentModule?.moduleVideo?.captions || []
        );
      }
      if (currentModule?.moduleFile?.length) {
        setCurrentContentFile(
          currentModule?.moduleFile?.map((moduleContentFile) => {
            const file: Required<AdminModuleContentForm>["file"][number] =
              new File(
                [new Blob([moduleContentFile?.location])],
                moduleContentFile?.fileName
              );
            file.__moduleContentFile = moduleContentFile;
            return file;
          })
        );
      }
    };

    const initialActivity = () => {
      if (currentModule?.activity?.length) {
        const activities: AdminActivity[] = [];
        currentModule?.activity?.forEach((activity) => {
          const type = activity?.type as AdminActivityType;

          switch (type) {
            case "flashcards":
            case "assessment":
            case "dragWord":
            case "trueFalse":
            case "quiz": {
              const questions = activity?.questions
                ?.questions as AdminFlashcardsFromApi["questions"];
              activities.push({
                id: activity?.id,
                attemptsNum: activity?.attemptsNum || 3,
                type,
                questions: { questions },
                image: questions?.map((q, index) => {
                  const file: ActivityImage = new File(
                    [new Blob([q?.image?.location])],
                    q?.image?.fileName
                  );
                  file.__activityImage = {
                    questionIndex: index,
                    location: q?.image?.location,
                    fileName: q?.image?.fileName,
                  };
                  return file;
                }),
              });
              break;
            }
            case "quizDragDrop": {
              const questions = activity?.questions
                ?.questions as AdminQuizDragAndDropFromApi["questions"];
              activities.push({
                id: activity?.id,
                attemptsNum: activity?.attemptsNum || 3,
                type,
                questions: {
                  questions: {
                    answers: questions?.answers?.map((a) => ({
                      ...(a || {}),
                      value: a?.value?.value,
                    })),
                  },
                },
                image: questions?.answers?.map((a, index) => {
                  const file: ActivityImage = new File(
                    [new Blob([a?.image?.image?.location])],
                    a?.image?.image?.fileName
                  );
                  file.__activityImage = {
                    questionIndex: index,
                    location: a?.image?.image?.location,
                    fileName: a?.image?.image?.fileName,
                  };
                  return file;
                }),
              });
              break;
            }
            case "dragDrop": {
              const questions = activity?.questions
                ?.questions as AdminActivity<"dragDrop">["questions"];
              activities.push({
                id: activity?.id,
                attemptsNum: activity?.attemptsNum || 3,
                type,
                questions: questions,
                //TODO: fix type
                image: questions?.groups?.map((q: any, index: number) => {
                  const file: ActivityImage = new File(
                    [new Blob([q?.image?.location])],
                    q?.image?.fileName
                  );
                  file.__activityImage = {
                    questionIndex: index,
                    location: q?.image?.location,
                    fileName: q?.image?.fileName,
                  };
                  return file;
                }),
              });
              break;
            }
            case "timeline":
            default: {
              const questions = activity?.questions
                ?.questions as AdminActivity["questions"];
              activities.push({
                id: activity?.id,
                attemptsNum: activity?.attemptsNum || 3,
                type,
                questions,
              });
            }
          }
        });
        setCurrentActivitiesObj(activities);
      }
    };

    const initialFeedback = () => {
      if (currentModule?.feedback) {
        const { apps, books, movies, podcasts, youtube } =
          currentModule.feedback;
        const appsObj = apps?.map((app) => {
          const { appImage, ...other } = app;
          return { image: appImage?.location, ...other };
        });
        const booksObj = books?.map((book) => {
          const { thumbnail, ...other } = book;
          return { image: thumbnail?.location, ...other };
        });
        const moviesObj = movies?.map((movie) => {
          const { movieImage, ...other } = movie;
          return { image: movieImage?.location, ...other };
        });
        const podcastsObj = podcasts?.map((podcast) => {
          const { podcastImage, ...other } = podcast;
          return { image: podcastImage?.location, ...other };
        });
        const youtubeObj = youtube?.map((val) => {
          const { videoImage, ...other } = val;
          return { image: videoImage?.location, ...other };
        });

        setCurrentFeedbackObj({
          app: appsObj,
          book: booksObj,
          movie: moviesObj,
          podcast: podcastsObj,
          youtube: youtubeObj,
        });
      }
    };

    if (currentModule !== null) {
      initialModuleInfo();
      initialModuleSurvey();
      initialModuleContent();
      initialActivity();
      initialFeedback();
    }
    setLoading(false);
  }, []);

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={styles.modalClassName}
      backdropClassName={styles.backdropClassName}
      withBackground={false}
    >
      {loading ? (
        <AscLoading loading={true} />
      ) : (
        <div
          data-testid="asc-activities-add-module"
          className={classNames(styles.ground, className)}
        >
          <CloseIcon className={styles.closeIcon} onClick={closeModal} />
          <AscHeading className={styles.header}>{header}</AscHeading>
          <AscAddModuleDetails value={value} />

          <div className={styles.contentGround}>
            <If test={value === 1}>
              <AscErrorBoundary>
                <AscAddModuleInfo
                  value={value}
                  setValue={setValue}
                  currentModuleInfo={currentModuleInfo}
                  header="Main module info"
                  handleModalData={handleModalData}
                />
              </AscErrorBoundary>
            </If>
            <If test={value === 2}>
              <AscErrorBoundary>
                <AscAddSurvey
                  value={value}
                  setValue={setValue}
                  header="Survey"
                  handleModalData={handleSurveyData}
                  currentSurveyInfo={currentSurveyInfo}
                />
              </AscErrorBoundary>
            </If>
            <If test={value === 3}>
              <AscErrorBoundary>
                <AscAddModuleContent
                  value={value}
                  setValue={setValue}
                  header="Content"
                  challenges={challenges}
                  addContent={handleContentData}
                  currentContentObj={currentContentObj}
                  currentContentFile={currentContentFile}
                  currentContentVideo={currentContentVideo}
                  currentContentVideoCaptions={currentContentVideoCaptions}
                />
              </AscErrorBoundary>
            </If>
            <If test={value === 4}>
              <AscErrorBoundary>
                <AscAddModuleActivities
                  value={value}
                  setValue={setValue}
                  header="Add activities"
                  addActivity={handleActivitiesData}
                  currentActivities={currentActivitiesObj}
                />
              </AscErrorBoundary>
            </If>
            <If test={value === 5}>
              <AscErrorBoundary>
                <AscAddFeedback
                  value={value}
                  header="Feedback"
                  setValue={setValue}
                  currentFeedbackObj={currentFeedbackObj}
                  handleFeedbackData={handleFeedbackData}
                  deleteFeedbackApp={deleteFeedbackApp}
                  deleteFeedbackBook={deleteFeedbackBook}
                  deleteFeedbackMovie={deleteFeedbackMovie}
                  deleteFeedbackPodcast={deleteFeedbackPodcast}
                  deleteFeedbackYoutube={deleteFeedbackYoutube}
                />
              </AscErrorBoundary>
            </If>
          </div>
        </div>
      )}
    </AscModal>
  );
}
